import { Flex, Heading, Skeleton, Text } from '@radix-ui/themes';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { PresetTrainingProvider } from 'components/machine/dialogs/preset-training/context';
import { PresetTrainingControls } from 'components/machine/dialogs/preset-training/controls';
import { AimingContext } from 'contexts/aiming.context';
import { TrainingContext, TrainingProvider } from 'contexts/training.context';
import { t } from 'i18next';
import { ITrainingDialog } from 'interfaces/i-training';
import { TrainingMode } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { HasId } from 'lib_ts/interfaces/common/i-extensions';
import { ITrainingPitch } from 'lib_ts/interfaces/pitches';
import { useContext, useEffect, useMemo } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useMiscStore from 'stores/use-misc-store';
import { v4 } from 'uuid';

const COMPONENT_NAME = 'PresetTrainingDialog';

interface IProps extends ITrainingDialog {
  updateTrainingPitch?: (
    // whatever was changed (e.g. _mx_ms after an iteration)
    payload: HasId & Partial<ITrainingPitch>
  ) => void;
}

export const PresetTrainingDialogHoC = (props: IProps) => {
  return (
    <TrainingProvider
      identifier="PTDialogTrainingCx"
      mode={TrainingMode.Quick}
      ignoreAfOnTr
      verbose
    >
      <PresetTrainingProvider>
        <PresetTrainingDialog {...props} />
      </PresetTrainingProvider>
    </TrainingProvider>
  );
};

const PresetTrainingDialog = (props: IProps) => {
  const { pitchTraining } = useContext(AimingContext);
  const { pitchIndex } = useContext(TrainingContext);

  const isInGame = useAuthStore((s) => s.isInGame);

  useEffect(() => {
    const id = v4();

    useMiscStore.getState().registerDialog({
      identifier: props.identifier,
      v4ID: id,
      open: true,
    });

    return () => {
      useMiscStore.getState().registerDialog({
        identifier: props.identifier,
        v4ID: id,
        open: false,
      });
    };
  }, []);

  if (isInGame) {
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
        <Heading size={RADIX.HEADING.SIZE.LG}>Game Status: {isInGame}</Heading>

        <p>
          Training is not possible at this time. Please try again later or
          contact support.
        </p>
      </Flex>
    </ErrorBoundary>;
  }

  const headingLabel = useMemo(() => {
    if (!props.pitches) {
      return t('common.loading');
    }

    return t('common.training-pitch-x-of-y', {
      x: pitchIndex + 1,
      y: props.pitches.length,
    });
  }, [props.pitches, pitchIndex]);

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        width={RADIX.DIALOG.WIDTH.XL}
        title={
          <Flex direction="column" gap="1">
            <Heading size={RADIX.HEADING.SIZE.MODAL_TITLE} truncate>
              {headingLabel}
            </Heading>

            <Skeleton loading={!pitchTraining}>
              <Text weight="medium" color={RADIX.COLOR.SECONDARY} size="2">
                {pitchTraining?.name ?? t('pl.unnamed-pitch')}
              </Text>
            </Skeleton>
          </Flex>
        }
        content={
          <PresetTrainingControls
            identifier="PTControls"
            pitches={props.pitches ?? []}
            updateTrainingPitch={props.updateTrainingPitch}
            onFinish={() => {
              props.onClose();
            }}
            // verbose
          />
        }
        onClose={() => {
          props.onClose();
        }}
        modal
      />
    </ErrorBoundary>
  );
};
